import React from 'react';
import { useTheme } from './theme';
import { useIsMobile } from './mobile';
const HoverPseudoKey = ":hover";
const FocusPseudoKey = ":focus";
const PseudoKeys = [HoverPseudoKey, FocusPseudoKey];
export function styled(component, styleSrc, baseProps) {
    return React.memo(function styled(props) {
        const ctxTheme = useTheme();
        const $theme = React.useMemo(() => ({
            ...ctxTheme,
            ...(props.themeOverride ?? {})
        }), [ctxTheme, props.themeOverride]);
        const $isMobile = useIsMobile();
        const [isHovering, setIsHovering] = React.useState(false);
        const [isFocused, setIsFocused] = React.useState(false);
        const onMouseEnter = React.useCallback((evt) => {
            setIsHovering(true);
            if ('onMouseEnter' in props && typeof props.onMouseEnter === 'function') {
                props.onMouseEnter?.(evt);
            }
        }, [props.onMouseEnter]);
        const onMouseLeave = React.useCallback((evt) => {
            setIsHovering(false);
            if ('onMouseLeave' in props && typeof props.onMouseLeave === 'function') {
                props.onMouseLeave?.(evt);
            }
        }, [props.onMouseLeave]);
        const onFocus = React.useCallback((evt) => {
            setIsFocused(true);
            if ('onFocus' in props && typeof props.onFocus === 'function') {
                props.onFocus?.(evt);
            }
        }, [props.onFocus]);
        const onBlur = React.useCallback((evt) => {
            setIsFocused(false);
            if ('onBlur' in props && typeof props.onBlur === 'function') {
                props.onBlur?.(evt);
            }
        }, [props.onBlur]);
        const resolvedStyleData = React.useMemo(() => {
            const compiledStyleObject = typeof styleSrc === 'function' ? styleSrc({ ...props, $theme, $isMobile }) : styleSrc;
            const resolvedStyleEntries = Object.entries(compiledStyleObject);
            const hoverStyle = resolvedStyleEntries.find(([key]) => key === HoverPseudoKey);
            const focusStyle = resolvedStyleEntries.find(([key]) => key === FocusPseudoKey);
            return { resolvedStyleEntries, hoverStyle, focusStyle, hash: JSON.stringify(compiledStyleObject) };
        }, [styleSrc, props, $theme, $isMobile]);
        const overrideStyleHash = React.useMemo(() => JSON.stringify(props.style), [props.style]);
        const style = React.useMemo(() => {
            const webStyleEntries = resolvedStyleData.resolvedStyleEntries.filter(([key]) => !PseudoKeys.includes(key));
            return {
                ...Object.fromEntries(webStyleEntries),
                // Place at end so it takes precedence
                ...(isHovering && resolvedStyleData.hoverStyle ? resolvedStyleData.hoverStyle[1] : {}),
                ...(isFocused && resolvedStyleData.focusStyle ? resolvedStyleData.focusStyle[1] : {}),
                // props.style overrides pseudo
                ...(props.style ?? {}),
            };
        }, [
            // Only recompute the style if the style data has actually changed
            resolvedStyleData.hash,
            // or if hover/focus state changes
            isHovering,
            isFocused,
            // or if direct style overrides change
            overrideStyleHash,
        ]);
        const resolvedProps = React.useMemo(() => {
            return {
                ...baseProps,
                ...Object.fromEntries(Object.entries(props).filter(([key]) => !['themeOverride', '_ref'].includes(key) && !key.startsWith("$"))),
                style,
                ref: props._ref,
                onMouseEnter,
                onMouseLeave,
                onFocus,
                onBlur
            };
        }, [baseProps, props, style, props._ref, onMouseEnter, onMouseLeave, onFocus, onBlur]);
        // @ts-expect-error it works leave it alone
        return React.createElement(component, resolvedProps, props.children);
    });
}
export function styledRN(component, styleSrc) {
    return React.forwardRef(function styledRN(props, ref) {
        const ctxTheme = useTheme();
        const $theme = {
            ...ctxTheme,
        };
        const $isMobile = useIsMobile();
        const componentStyle = React.useMemo(() => {
            return {
                ...(typeof styleSrc === 'function' ? styleSrc({ ...props, $theme, $isMobile }) : styleSrc),
                ...props.style ?? {}
            };
        }, [props, styleSrc, $theme, $isMobile]);
        // @ts-expect-error it works leave it alone
        return React.createElement(component, {
            ...props,
            style: componentStyle,
            ref,
            // @ts-expect-error it works leave it alone
        }, props.children);
    });
}
